import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, Menu, Modal, notification } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faEye, faGift } from "@fortawesome/free-solid-svg-icons";

import SmartRtpTablePage from "./components/SmartRtpTablePage";
import SmartRtpCards from "./components/SmartRtpCards";
import { REDEEM_STATUS_TABS, WINNER_SEARCH_NUMERIC_FIELDS, WINNER_SEARCH_OPTIONS } from "./config";
import { TZ } from "../../../config/constants";
import { formatSmartRtpNumber } from "../../../utils/bigMath";
import {
  adminRedeemWinner,
  getWinnersDaySummary,
  getWinnersList,
} from "../../../controllers/V1/smartRtp/smartRtpController";

function formatBahrainDateTime(value) {
  if (!value) return "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(value));
  const get = (type) => parts.find((part) => part.type === type)?.value || "";
  return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")}:${get("second")}`;
}

function canRedeemWinner(row) {
  const status = row?.redeem_status;
  if (status !== "Pending" && status !== "OfficeClaim") return false;
  return Boolean(row?.ticket_id || row?._id);
}

function WinnerActionsMenu({ row, onRedeem, redeemingKey }) {
  const rowKey = row?.reward_id || row?.ticket_id || row?.order_no;
  const isRedeeming = redeemingKey === rowKey;
  const redeemable = canRedeemWinner(row);

  const confirmRedeem = () => {
    Modal.confirm({
      title: "Mark winner as redeemed?",
      content: `Order ${row?.order_no || "-"} will be marked as redeemed from admin.`,
      okText: "Redeem",
      cancelText: "Cancel",
      onOk: () => onRedeem(row),
    });
  };

  return (
    <Dropdown
      placement="bottomLeft"
      arrow
      overlay={
        <Menu>
          {row?.order_id ? (
            <Menu.Item key="view">
              <Link to={`/smart-rtp/orders/details?editId=${String(row.order_id)}`}>
                <FontAwesomeIcon icon={faEye} /> <span className="show_span">View Order</span>
              </Link>
            </Menu.Item>
          ) : null}
          <Menu.Item
            key="redeem"
            disabled={!redeemable || isRedeeming}
            onClick={confirmRedeem}
          >
            <FontAwesomeIcon icon={faGift} />{" "}
            <span className="show_span">{isRedeeming ? "Redeeming..." : "Redeem"}</span>
          </Menu.Item>
        </Menu>
      }
    >
      <FontAwesomeIcon icon={faEllipsis} style={{ cursor: "pointer" }} />
    </Dropdown>
  );
}

export default function SmartRtpWinnersDetailsPage() {
  const [searchParams] = useSearchParams();
  const filterDate = searchParams.get("date") || moment().format("YYYY-MM-DD");
  const [summary, setSummary] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [redeemingKey, setRedeemingKey] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    const res = await getWinnersDaySummary({ filter_date: filterDate });
    if (res.status === true) {
      setSummary(res.result || {});
    }
    setLoadingSummary(false);
  }, [filterDate]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, refreshToken]);

  const handleRedeem = useCallback(async (row) => {
    const ticketId = row?.ticket_id || row?._id;
    const rowKey = row?.reward_id || ticketId || row?.order_no;
    if (!row?.order_no) {
      notification.warning({ message: "Order number is missing for this winner." });
      return;
    }
    if (!ticketId) {
      notification.warning({ message: "Ticket reference is missing for this winner." });
      return;
    }
    setRedeemingKey(rowKey);
    try {
      const res = await adminRedeemWinner({
        order_no: row.order_no,
        ticket_id: ticketId,
        ...(row.reward_id && String(row.reward_id) !== String(ticketId)
          ? { reward_id: row.reward_id }
          : {}),
      });
      if (res?.status === true) {
        notification.success({
          message: "Winner redeemed",
          description: `Order ${row.order_no} marked as redeemed.`,
        });
        setRefreshToken((prev) => prev + 1);
      } else {
        notification.error({
          message: "Redeem failed",
          description: res?.message || "Unable to redeem this winner.",
        });
      }
    } finally {
      setRedeemingKey(null);
    }
  }, []);

  const cards = useMemo(
    () => [
      { key: "winners", label: "Total Winners", value: summary.total_winners || 0 },
      {
        key: "winning",
        label: "Total Winning Amount",
        value: summary.total_winning_amount || 0,
      },
      {
        key: "redeemed",
        label: "Total Redeemed Amount",
        value: summary.total_redeemed_amount || 0,
      },
      {
        key: "unpaid",
        label: "Total Amount Unpaid",
        value: summary.total_unpaid_amount || 0,
      },
    ],
    [summary]
  );

  const displayDate = moment(filterDate, "YYYY-MM-DD").format("DD-MM-YYYY");

  const columns = useMemo(
    () => [
      { key: "order_no", label: "Order No", showInExcel: true, showInOrderList: true },
      { key: "ticket_index", label: "Ticket #", showInExcel: true, showInOrderList: true },
      { key: "game_title", label: "Game", showInExcel: true, showInOrderList: true },
      {
        key: "ticket_numbers",
        label: "Ticket Numbers",
        showInExcel: true,
        showInOrderList: true,
        render: (row) =>
          Array.isArray(row?.ticket_numbers) && row.ticket_numbers.length
            ? row.ticket_numbers.join(", ")
            : "-",
        exportValue: (row) =>
          Array.isArray(row?.ticket_numbers) ? row.ticket_numbers.join(", ") : "",
      },
      {
        key: "reward_amount",
        label: "Winning Amount",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => {
          if (row?.prize_kind === "Product" || (row?.product_name && !(Number(row?.reward_amount) > 0))) {
            return row.product_name || "-";
          }
          const amount = formatSmartRtpNumber(row?.reward_amount || 0, 3);
          if (row?.product_name) return `${amount} + ${row.product_name}`;
          return amount;
        },
        exportValue: (row) => {
          if (row?.prize_kind === "Product" || (row?.product_name && !(Number(row?.reward_amount) > 0))) {
            return row.product_name || "";
          }
          if (row?.product_name) return `${row?.reward_amount || 0} + ${row.product_name}`;
          return row?.reward_amount || 0;
        },
      },
      {
        key: "match_count",
        label: "Matches",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => row?.match_count || 0,
        exportValue: (row) => row?.match_count || 0,
      },
      {
        key: "redeem_status",
        label: "Redeem Status",
        showInExcel: true,
        showInOrderList: true,
      },
      {
        key: "seller_pos_number",
        label: "POS Number",
        showInExcel: true,
        showInOrderList: true,
        render: (row) =>
          row?.seller?.pos_number != null && row.seller.pos_number !== ""
            ? row.seller.pos_number
            : row?.seller_pos_number ?? "-",
        exportValue: (row) => row?.seller?.pos_number ?? row?.seller_pos_number ?? "",
      },
      {
        key: "seller_name",
        label: "Seller",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => {
          if (!row?.seller) return "-";
          const name = `${row.seller.first_name || ""} ${row.seller.last_name || ""}`.trim();
          return name || "-";
        },
        exportValue: (row) => {
          if (!row?.seller) return "";
          return `${row.seller.first_name || ""} ${row.seller.last_name || ""}`.trim();
        },
      },
      {
        key: "createdAt",
        label: "Created at",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => formatBahrainDateTime(row?.createdAt),
        exportValue: (row) =>
          row?.createdAt ? formatBahrainDateTime(row.createdAt) : "",
      },
      {
        key: "redeem_by",
        label: "Redeem By POS",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => {
          if (!row?.redeem_by) return "-";
          const name = `${row.redeem_by.pos_number || ""}`.trim();
          return name || "-";
        },
        exportValue: (row) => {
          if (!row?.redeem_by) return "";
          return `${row.redeem_by.pos_number || ""}`.trim();
        },
      },
      {
        key: "redeem_date_time",
        label: "Redeemed At",
        showInExcel: true,
        showInOrderList: true,
        render: (row) => formatBahrainDateTime(row?.redeem_date_time),
        exportValue: (row) =>
          row?.redeem_date_time ? formatBahrainDateTime(row.redeem_date_time) : "",
      },
      {
        key: "actions",
        label: "Actions",
        showInExcel: false,
        showInOrderList: true,
        render: (row) => (
          <WinnerActionsMenu row={row} onRedeem={handleRedeem} redeemingKey={redeemingKey} />
        ),
      },
    ],
    [handleRedeem, redeemingKey]
  );

  return (
    <SmartRtpTablePage
      title={`Smart Winners — ${displayDate}`}
      activeKey="winners"
      fetchData={getWinnersList}
      statusTabs={REDEEM_STATUS_TABS}
      statusField="redeem_status"
      searchFields={WINNER_SEARCH_OPTIONS.map((option) => option.value)}
      searchOptions={WINNER_SEARCH_OPTIONS}
      searchNumericFields={WINNER_SEARCH_NUMERIC_FIELDS}
      showFilter
      showExport
      exportFileName={`Smart Winners ${filterDate}`}
      exportConfig={{ hideSearch: false }}
      refreshToken={refreshToken}
      buildOptions={({ skip, limit, statusValue }) => ({
        filter_date: filterDate,
        redeem_status: statusValue || undefined,
        skip,
        limit,
      })}
      actions={
        <Link to="/smart-rtp/winners" className="btn btn-light">
          Back to Daily Summary
        </Link>
      }
      extraTop={
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <div className="text-muted small">
                Winning tickets for {displayDate} ({TZ}, 00:00:00 – 23:59:59)
              </div>
            </div>
          </div>
          <SmartRtpCards cards={cards} />
          {loadingSummary ? (
            <div className="small text-muted mb-3">Loading summary...</div>
          ) : null}
        </>
      }
      columns={columns}
    />
  );
}
